import React from 'react';
import PublicLayout from '../../components/layouts/PublicLayout';
import { usePublicTranslation } from '../../hooks/usePublicTranslation';
import { useSEO, generateBreadcrumbSchema, generateWebPageSchema } from '../../hooks/useSEO';
import { ArrowLeft } from 'lucide-react';

const CookiePolicy: React.FC = () => {
    const { t, currentLanguage } = usePublicTranslation();
    const legal = t.legal || {};
    const content = legal.cookieContent || {};

    useSEO({
        title: legal.seo?.cookies?.title || "Politica de Cookies - NexuCapital",
        description: legal.seo?.cookies?.description || "Informacoes sobre como utilizamos cookies em nosso site.",
        path: '/cookie-policy',
        language: currentLanguage as 'pt-br' | 'en' | 'es',
        noindex: false,
        structuredData: [
            generateBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Legal', url: '/legal' },
                { name: 'Cookie Policy', url: '/cookie-policy' }
            ]),
            generateWebPageSchema({
                name: legal.seo?.cookies?.title || "Politica de Cookies - NexuCapital",
                description: legal.seo?.cookies?.description || "Informacoes sobre como utilizamos cookies em nosso site.",
                url: '/cookie-policy',
                datePublished: "2025-01-20",
                dateModified: "2025-01-20"
            })
        ]
    });

    return (
        <PublicLayout>

            <section className="pt-24 pb-16 bg-gray-50 min-h-screen">
                <div className="container mx-auto px-4">
                    {/* Back Link */}
                    <a
                        href="/"
                        className="inline-flex items-center text-primary hover:text-primary-dark mb-6 transition-colors"
                    >
                        <ArrowLeft size={18} className="mr-2" />
                        {t.navigation?.home || "Inicio"}
                    </a>

                    {/* Content Card */}
                    <div className="bg-white rounded-xl shadow-sm p-6 sm:p-8 md:p-12 max-w-4xl mx-auto">
                        {/* Header */}
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-primary mb-4 pb-4 border-b-2 border-secondary">
                            {legal.cookiePolicy || "Politica de Cookies"}
                        </h1>

                        <p className="text-gray-600 mb-8">
                            <strong>{legal.lastUpdated || "Ultima atualizacao"}:</strong> {legal.lastUpdatedDate || "01 de setembro de 2025"}
                        </p>

                        {/* Section 1 - What are Cookies */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section1?.title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section1?.text}
                        </p>

                        {/* Section 2 - Cookies We Use */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section2?.title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section2?.intro}
                        </p>
                        <ul className="space-y-6 text-gray-700 mb-8">
                            <li className="bg-gray-50 p-4 rounded-lg">
                                <strong className="text-dark block mb-2">{content.section2?.cookies?.essential?.title}</strong>
                                <p className="mb-2">{content.section2?.cookies?.essential?.description}</p>
                                <p className="text-sm text-gray-500 italic">{content.section2?.cookies?.essential?.examples}</p>
                            </li>
                            <li className="bg-gray-50 p-4 rounded-lg">
                                <strong className="text-dark block mb-2">{content.section2?.cookies?.performance?.title}</strong>
                                <p className="mb-2">{content.section2?.cookies?.performance?.description}</p>
                                <p className="text-sm text-gray-500 italic">{content.section2?.cookies?.performance?.examples}</p>
                            </li>
                            <li className="bg-gray-50 p-4 rounded-lg">
                                <strong className="text-dark block mb-2">{content.section2?.cookies?.functionality?.title}</strong>
                                <p className="mb-2">{content.section2?.cookies?.functionality?.description}</p>
                                <p className="text-sm text-gray-500 italic">{content.section2?.cookies?.functionality?.examples}</p>
                            </li>
                            <li className="bg-gray-50 p-4 rounded-lg">
                                <strong className="text-dark block mb-2">{content.section2?.cookies?.thirdParty?.title}</strong>
                                <p className="mb-2">{content.section2?.cookies?.thirdParty?.description}</p>
                                <p className="text-sm text-gray-500 italic">{content.section2?.cookies?.thirdParty?.examples}</p>
                            </li>
                        </ul>

                        {/* Section 3 - Cookie Table */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section3?.title}
                        </h2>
                        <div className="overflow-x-auto mb-8">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-primary text-white">
                                        {content.section3?.tableHeaders?.map((header: string, index: number) => (
                                            <th key={index} className="px-4 py-3 text-left font-semibold text-sm">
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {content.section3?.tableData?.map((row: string[], rowIndex: number) => (
                                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                                            {row.map((cell: string, cellIndex: number) => (
                                                <td key={cellIndex} className="px-4 py-3 text-sm text-gray-700 border-b border-gray-200">
                                                    {cellIndex === 0 ? <code className="bg-gray-100 px-2 py-1 rounded text-xs">{cell}</code> : cell}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Section 4 - Cookie Management */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section4?.title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-8">
                            {content.section4?.text}
                        </p>

                        {/* Section 5 - How to Disable Cookies */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section5?.title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed mb-4">
                            {content.section5?.text}
                        </p>
                        <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-8">
                            {content.section5?.optOutLinks?.map((link: { name: string; url: string }, index: number) => (
                                <li key={index}>
                                    {link.url ? (
                                        <a
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:text-primary-dark underline transition-colors"
                                        >
                                            {link.name}
                                        </a>
                                    ) : (
                                        <span>{link.name}</span>
                                    )}
                                </li>
                            ))}
                        </ul>

                        {/* Section 6 - More Information */}
                        <h2 className="text-xl sm:text-2xl font-bold text-dark mt-8 mb-4">
                            {content.section6?.title}
                        </h2>
                        <p className="text-gray-700 leading-relaxed">
                            {content.section6?.text}{' '}
                            <a
                                href="/privacy-policy"
                                className="text-primary hover:text-primary-dark underline transition-colors"
                            >
                                {content.section6?.privacyPolicyLink}
                            </a>.
                        </p>
                    </div>
                </div>
            </section>
        </PublicLayout>
    );
};

export default CookiePolicy;
